import React, { useEffect, useState } from "react";
import { ArrowLeft, Power, PowerOff, Trash2, CalendarRange, Clock, User, Calendar } from "lucide-react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AutoSaveForm } from "@/components/forms/AutoSaveForm";
import { useAppToast } from "@/hooks/useAppToast";
import { createVouchersService } from "@/services/admin/vouchers/vouchers.service";
import {
  Voucher,
  UpdateVoucherInput,
  VoucherKind,
} from "@/services/admin/vouchers/vouchers.interfaces";

const normalizeVoucherKind = (value: unknown): VoucherKind => {
  if (value === "time" || value === "Time" || value === 1 || value === "1") return "time";
  return "discount";
};
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useLoading } from "@/lib/LoadingHook";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const vouchersService = createVouchersService();

interface VoucherBasicInfoFormProps {
  voucherData: Voucher;
  onSave: (data: Partial<UpdateVoucherInput>) => Promise<void>;
}

const VoucherBasicInfoForm: React.FC<VoucherBasicInfoFormProps> = ({
  voucherData,
  onSave,
}) => {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold mb-4">Informações Básicas</h2>
      <AutoSaveForm onSave={onSave} className="space-y-4">
        <Card className="p-6 space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Código do Voucher</Label>
              <Input
                id="code"
                name="code"
                type="text"
                defaultValue={voucherData.code}
                disabled
                className="bg-gray-50"
                title="Código não pode ser alterado"
              />
              <p className="text-xs text-gray-500">O código do voucher não pode ser alterado</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                name="name"
                type="text"
                defaultValue={voucherData.name}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                name="description"
                className="h-32"
                defaultValue={voucherData.description || ''}
              />
            </div>
            <div className="flex items-center space-x-2">
              <div className="text-xs text-gray-500 mb-2">
                O status do voucher pode ser alterado através da toolbar na parte superior da página.
              </div>
            </div>
          </div>
        </Card>
      </AutoSaveForm>
    </div>
  );
};

interface VoucherDiscountFormProps {
  voucherData: Voucher;
  onSave: (data: Partial<UpdateVoucherInput>) => Promise<void>;
}

const VoucherDiscountForm: React.FC<VoucherDiscountFormProps> = ({
  voucherData,
  onSave,
}) => {
  const [discountType, setDiscountType] = useState(voucherData.discountType);
  const [voucherKind, setVoucherKind] = useState<VoucherKind>(
    normalizeVoucherKind(voucherData.voucherKind)
  );

  const persistKind = async (next: VoucherKind) => {
    setVoucherKind(next);
    if (next === "discount") {
      await onSave({ voucherKind: "discount", durationDays: null });
      return;
    }
    await onSave({
      voucherKind: "time",
      durationDays: voucherData.durationDays && voucherData.durationDays > 0
        ? voucherData.durationDays
        : undefined,
    });
  };

  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold mb-4">Tipo e Benefício</h2>
      <AutoSaveForm onSave={onSave} className="space-y-4">
        <Card className="p-6 space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="voucherKind">Tipo de voucher</Label>
              <input type="hidden" name="voucherKind" value={voucherKind} />
              <Select
                value={voucherKind}
                onValueChange={(value) => persistKind(value as VoucherKind)}
              >
                <SelectTrigger id="voucherKind">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="discount">Desconto</SelectItem>
                  <SelectItem value="time">Tempo de acesso</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {voucherKind === "time" ? (
              <div className="space-y-2">
                <Label htmlFor="durationDays">Duração (dias)</Label>
                <Input
                  id="durationDays"
                  name="durationDays"
                  type="number"
                  min="1"
                  defaultValue={voucherData.durationDays ?? ""}
                />
              </div>
            ) : (
              <>
            <div className="space-y-2">
              <Label htmlFor="discountType">Tipo de Desconto</Label>
              <Select
                name="discountType"
                defaultValue={voucherData.discountType}
                onValueChange={(value) => setDiscountType(value as any)}
              >
                <SelectTrigger id="discountType">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentual (%)</SelectItem>
                  <SelectItem value="fixedAmount">Valor Fixo (R$)</SelectItem>
                  <SelectItem value="freeShipping">Frete Grátis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {discountType !== 'freeShipping' && (
              <div className="space-y-2">
                <Label htmlFor="discountValue">
                  Valor do Desconto {discountType === 'percentage' ? '(%)' : '(R$)'}
                </Label>
                <Input
                  id="discountValue"
                  name="discountValue"
                  type="number"
                  min="0"
                  max={discountType === 'percentage' ? "100" : undefined}
                  step={discountType === 'percentage' ? "0.1" : "0.01"}
                  defaultValue={voucherData.discountValue}
                />
              </div>
            )}

            {discountType === 'freeShipping' && (
              <div className="flex items-center space-x-2">
                <Switch
                  id="freeShipping"
                  name="freeShipping"
                  defaultChecked={voucherData.freeShipping || false}
                />
                <Label htmlFor="freeShipping">Ativar frete grátis</Label>
              </div>
            )}
              </>
            )}
          </div>
        </Card>
      </AutoSaveForm>
    </div>
  );
};

interface VoucherLimitsFormProps {
  voucherData: Voucher;
  onSave: (data: Partial<UpdateVoucherInput>) => Promise<void>;
}

const VoucherLimitsForm: React.FC<VoucherLimitsFormProps> = ({
  voucherData,
  onSave,
}) => {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold mb-4">Limites e Validade</h2>
      <AutoSaveForm onSave={onSave} className="space-y-4">
        <Card className="p-6 space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="maxUses">Uso Máximo Global</Label>
              <Input
                id="maxUses"
                name="maxUses"
                type="number"
                min="0"
                defaultValue={voucherData.maxUses || ''}
                placeholder="Ilimitado"
              />
              <p className="text-xs text-gray-500">Deixe vazio para uso ilimitado</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="maxUsesPerUser">Uso Máximo por Usuário</Label>
              <Input
                id="maxUsesPerUser"
                name="maxUsesPerUser"
                type="number"
                min="0"
                defaultValue={voucherData.maxUsesPerUser || ''}
                placeholder="Ilimitado"
              />
              <p className="text-xs text-gray-500">Deixe vazio para uso ilimitado por usuário</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="validFrom">Data de Início</Label>
              <Input
                id="validFrom"
                name="validFrom"
                type="datetime-local"
                defaultValue={voucherData.validFrom}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="validUntil">Data de Fim</Label>
              <Input
                id="validUntil"
                name="validUntil"
                type="datetime-local"
                defaultValue={voucherData.validUntil}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Select name="timezone" defaultValue={voucherData.timezone || 'UTC'}>
                <SelectTrigger id="timezone">
                  <SelectValue placeholder="Selecione o timezone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UTC">UTC</SelectItem>
                  <SelectItem value="America/Sao_Paulo">America/Sao_Paulo</SelectItem>
                  <SelectItem value="America/New_York">America/New_York</SelectItem>
                  <SelectItem value="Europe/London">Europe/London</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>
      </AutoSaveForm>
    </div>
  );
};

const VoucherRestrictionsForm: React.FC = () => {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold mb-4">Restrições</h2>
      <Card className="p-6">
        <div className="text-center py-8 text-gray-500">
          <p className="text-lg">Configuração de restrições será implementada em breve</p>
          <p className="text-sm mt-2">Por enquanto, o voucher será aplicável a todos os pacotes</p>
        </div>
      </Card>
    </div>
  );
};

const AdminEditVoucherPage: React.FC = () => {
  const { voucherId } = useParams<{
    voucherId: string;
  }>();
  const navigate = useNavigate();
  const toast = useAppToast();
  const { isLoading: isVoucherLoading, whenLoading: whenVoucherLoading } = useLoading();
  const { isLoading: isStatusLoading, whenLoading: whenStatusLoading } = useLoading();
  const { isLoading: isDeleteLoading, whenLoading: whenDeleteLoading } = useLoading();
  const { isLoading: isExtendLoading, whenLoading: whenExtendLoading } = useLoading();
  
  const [voucherData, setVoucherData] = useState<Voucher | null>(null);
  
  // Estados para diálogos
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [newValidUntil, setNewValidUntil] = useState("");

  useEffect(() => {
    const loadVoucher = async () => {
      console.log("Loading voucher with voucherId:", voucherId);
      if (!voucherId) {
        console.log("Missing voucherId");
        return;
      }

      await whenVoucherLoading(async () => {
        try {
          const data = await vouchersService.getVoucherById(voucherId);
          setVoucherData(data);
          // Inicializar a data de validade com a data atual do voucher
          if (data.validUntil) {
            setNewValidUntil(data.validUntil.substring(0, 16)); // Formato YYYY-MM-DDTHH:MM
          }
        } catch (error) {
          toast.error("Falha ao carregar dados do voucher");
          console.error("Error loading voucher:", error);
        }
      });
    };

    loadVoucher();
  }, [voucherId]);

  const handleSave = async (changes: Partial<UpdateVoucherInput>) => {
    if (!voucherId) return;

    const payload: Partial<UpdateVoucherInput> = { ...changes };
    if (payload.voucherKind) {
      payload.voucherKind = normalizeVoucherKind(payload.voucherKind);
    }
    if (payload.durationDays !== undefined && payload.durationDays !== null) {
      const days = Number(payload.durationDays);
      payload.durationDays = Number.isFinite(days) && days > 0 ? days : null;
    }
    if (payload.voucherKind === "discount") {
      payload.durationDays = null;
    }

    try {
      await vouchersService.updateVoucher(voucherId, payload);
      setVoucherData((prev) => (prev ? { ...prev, ...payload } : null));
      toast.success("Alterações salvas!");
    } catch (error) {
      toast.error("Erro ao salvar alterações.");
      console.error("Error updating voucher:", error);
      throw error;
    }
  };
  
  // Função para ativar/desativar voucher
  const handleToggleStatus = async () => {
    if (!voucherId || !voucherData) return;
    
    await whenStatusLoading(async () => {
      try {
        const updatedVoucher = voucherData.isActive 
          ? await vouchersService.deactivateVoucher(voucherId)
          : await vouchersService.activateVoucher(voucherId);
        
        setVoucherData(updatedVoucher);
        toast.success(`Voucher ${voucherData.isActive ? "desativado" : "ativado"} com sucesso!`);
      } catch (error) {
        toast.error(`Erro ao ${voucherData.isActive ? "desativar" : "ativar"} o voucher.`);
        console.error("Error toggling voucher status:", error);
      }
    });
  };
  
  // Função para excluir voucher
  const handleDelete = async () => {
    if (!voucherId) return;
    
    await whenDeleteLoading(async () => {
      try {
        await vouchersService.deleteVoucher(voucherId);
        toast.success("Voucher excluído com sucesso!");
        navigate("/admin/vouchers");
      } catch (error) {
        toast.error("Erro ao excluir o voucher.");
        console.error("Error deleting voucher:", error);
      }
    });
  };
  
  // Função para estender validade
  const handleExtendValidity = async () => {
    if (!voucherId || !newValidUntil) {
      toast.error("Selecione uma nova data de validade.");
      return;
    }
    
    await whenExtendLoading(async () => {
      try {
        const updatedVoucher = await vouchersService.extendVoucherValidity(voucherId, newValidUntil);
        setVoucherData(updatedVoucher);
        toast.success("Data de validade atualizada com sucesso!");
        setShowExtendDialog(false);
      } catch (error) {
        toast.error("Erro ao atualizar a data de validade.");
        console.error("Error extending voucher validity:", error);
      }
    });
  };

  if (isVoucherLoading) {
    return (
      <AdminPageLayout title="Carregando...">
        <div className="flex items-center justify-center py-8">Carregando...</div>
      </AdminPageLayout>
    );
  }

  if (!voucherData) {
    return (
      <AdminPageLayout title="Voucher Não Encontrado">
        <div className="flex items-center justify-center py-8">
          Voucher não encontrado
        </div>
      </AdminPageLayout>
    );
  }

  const BackButtons = () => {
    return (
      <div className="flex gap-4 mb-4">
        <Link
          to="/admin/vouchers"
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para Vouchers
        </Link>
      </div>
    );
  };

  // VoucherToolbar - Componente para operações críticas
  const VoucherToolbar = () => {
    return (
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowStatusDialog(true)}
            disabled={isStatusLoading}
          >
            {voucherData?.isActive ? (
              <><PowerOff className="w-4 h-4 mr-2" />Desativar Voucher</>
            ) : (
              <><Power className="w-4 h-4 mr-2" />Ativar Voucher</>
            )}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowExtendDialog(true)}
            disabled={isExtendLoading}
          >
            <CalendarRange className="w-4 h-4 mr-2" />Estender Validade
          </Button>
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={() => setShowDeleteDialog(true)}
            disabled={isDeleteLoading}
          >
            <Trash2 className="w-4 h-4 mr-2" />Excluir Voucher
          </Button>
        </div>
      </div>
    );
  };
  
  // Diálogos de confirmação
  const renderDialogs = () => (
    <>
      {/* Status Dialog */}
      <ConfirmDialog
        open={showStatusDialog}
        onOpenChange={setShowStatusDialog}
        title={voucherData?.isActive ? "Desativar Voucher" : "Ativar Voucher"}
        description={`Tem certeza que deseja ${voucherData?.isActive ? "desativar" : "ativar"} o voucher "${voucherData?.name}"?`}
        confirmText={voucherData?.isActive ? "Desativar" : "Ativar"}
        cancelText="Cancelar"
        variant={voucherData?.isActive ? "default" : "default"}
        onConfirm={handleToggleStatus}
        loading={isStatusLoading}
      />
      
      {/* Delete Dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Excluir Voucher"
        description={`Tem certeza que deseja excluir o voucher "${voucherData?.name}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="destructive"
        onConfirm={handleDelete}
        loading={isDeleteLoading}
      />
      
      {/* Extend Validity Dialog */}
      <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Estender Validade</DialogTitle>
            <DialogDescription>Defina a nova data de validade para o voucher</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newValidUntil">Nova Data de Validade</Label>
              <Input
                id="newValidUntil"
                type="datetime-local"
                value={newValidUntil}
                onChange={(e) => setNewValidUntil(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowExtendDialog(false)}
              disabled={isExtendLoading}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleExtendValidity}
              disabled={isExtendLoading || !newValidUntil}
            >
              {isExtendLoading ? "Salvando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // Componente para mostrar estatísticas do voucher
  const VoucherStatusPanel = () => {
    // Calcular porcentagem de uso
    const usagePercentage = voucherData.maxUses 
      ? Math.round((voucherData.currentUses / voucherData.maxUses) * 100) 
      : 0;
    
    // Determinar a cor do badge baseado no status
    const getBadgeVariant = () => {
      switch (voucherData.status) {
        case 'Active': return 'success';
        case 'Inactive': return 'secondary';
        case 'Expired': return 'warning';
        default: return 'default';
      }
    };
    
    // Formatar data para exibição
    const formatDate = (dateString: string) => {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString();
    };
    
    return (
      <Card className="mb-6 sticky top-4">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Status do Voucher</CardTitle>
            <Badge variant={getBadgeVariant()}>
              {voucherData.status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Estatísticas de uso */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Uso: {voucherData.currentUses}/{voucherData.maxUses || '∞'}</span>
              <span>{voucherData.maxUses ? 
                `(${usagePercentage}%)` : ''}
              </span>
            </div>
            <Progress 
              value={usagePercentage} 
              className="h-2" 
            />
          </div>
          
          {/* Informações adicionais */}
          <div className="pt-2">
            <h4 className="text-sm font-medium mb-2">Detalhes</h4>
            <dl className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-gray-400" />
                <dt className="text-gray-500">Criado em:</dt>
                <dd>{formatDate(voucherData.createdOn)}</dd>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-gray-400" />
                <dt className="text-gray-500">Válido até:</dt>
                <dd>{formatDate(voucherData.validUntil)}</dd>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-gray-400" />
                <dt className="text-gray-500">Por usuário:</dt>
                <dd>{voucherData.maxUsesPerUser || '∞'}</dd>
              </div>
            </dl>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <AdminPageLayout title={`Editar ${voucherData.name}`}>
      <BackButtons />
      <VoucherToolbar />
      {renderDialogs()}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        {/* Coluna principal - Formulários com abas */}
        <div className="md:col-span-2">
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="w-full justify-start mb-4">
              <TabsTrigger value="basic">Informações Básicas</TabsTrigger>
              <TabsTrigger value="discount">Tipo e Benefício</TabsTrigger>
              <TabsTrigger value="limits">Limites e Validade</TabsTrigger>
              <TabsTrigger value="restrictions">Restrições</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="mt-0">
              <VoucherBasicInfoForm voucherData={voucherData} onSave={handleSave} />
            </TabsContent>
            
            <TabsContent value="discount" className="mt-0">
              <VoucherDiscountForm voucherData={voucherData} onSave={handleSave} />
            </TabsContent>
            
            <TabsContent value="limits" className="mt-0">
              <VoucherLimitsForm voucherData={voucherData} onSave={handleSave} />
            </TabsContent>
            
            <TabsContent value="restrictions" className="mt-0">
              <VoucherRestrictionsForm />
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Coluna lateral - Status e estatísticas */}
        <div>
          <VoucherStatusPanel />
        </div>
      </div>
    </AdminPageLayout>
  );
};

export default AdminEditVoucherPage;
